import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

import styles from './infoTip.module.css';

/**
 * A small grey round “i” with a hint: it shows on hover and on keyboard
 * focus, and a tap toggles it on a phone. Escape or a tap elsewhere hides it.
 */
export function InfoTip({ label, children, testId }: { readonly label: string; readonly children: ReactNode; readonly testId?: string }): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  const tip = useRef<HTMLSpanElement>(null);
  const id = useId();

  // Keep the hint inside the nearest box that clips it, else inside the window: slide it sideways
  useLayoutEffect(() => {
    const el = tip.current;
    if (!open || !el) return;
    el.style.setProperty('--tip-shift', '0px');
    let left = 8;
    let right = window.innerWidth - 8;
    for (let parent = el.parentElement; parent; parent = parent.parentElement) {
      if (getComputedStyle(parent).overflowX !== 'visible') {
        const box = parent.getBoundingClientRect();
        left = Math.max(left, box.left + 4);
        right = Math.min(right, box.right - 4);
        break;
      }
    }
    const rect = el.getBoundingClientRect();
    let shift = 0;
    if (rect.right > right) shift = right - rect.right;
    if (rect.left + shift < left) shift = left - rect.left;
    el.style.setProperty('--tip-shift', `${String(Math.round(shift))}px`);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    const onPointer = (event: PointerEvent): void => {
      if (event.target instanceof Node && wrap.current && !wrap.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [open]);

  return (
    <span
      ref={wrap}
      className={styles.wrap}
      onPointerEnter={(event) => {
        if (event.pointerType === 'mouse') setOpen(true);
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === 'mouse') setOpen(false);
      }}
    >
      <button
        type="button"
        className={styles.button}
        aria-label={label}
        aria-describedby={open ? id : undefined}
        aria-expanded={open}
        onClick={() => {
          setOpen((value) => !value);
        }}
        onFocus={(event) => {
          // the keyboard opens it; a tap focuses too, and its click toggles it
          if (event.currentTarget.matches(':focus-visible')) setOpen(true);
        }}
        onBlur={() => {
          setOpen(false);
        }}
        data-testid={testId}
      >
        <span aria-hidden="true">i</span>
      </button>
      {open ? (
        <span ref={tip} id={id} role="tooltip" className={styles.tip}>
          {children}
        </span>
      ) : null}
    </span>
  );
}
